import Peer from 'peerjs';

let peer = null;
let myPeerId = null;
let initializePromise = null; // Store the promise of the current/last initialization
const connections = {}; // Store open connections { peerId: DataConnection }
let onDataReceivedCallback = null; // Callback for when data is received from a peer
let onPeerDisconnectedCallback = null; // Callback for when a peer connection closes or times out

const PING_INTERVAL = 10000; // Send a ping every 10 seconds
const PONG_TIMEOUT = 25000; // Expect a pong within 25 seconds of last contact
const PEER_PRUNE_GRACE_MS = 20000; // Wait before declaring a peer gone after DataConnection close
let pingIntervalId = null;
let timeoutCheckIntervalId = null;
const lastContactTimestamps = {}; // { peerId: timestamp }
const pendingDisconnectTimers = {}; // { peerId: timeoutId }

// Add server keepalive variables
let serverKeepAliveIntervalId = null;
const SERVER_KEEPALIVE_INTERVAL = 30000; // Send keepalive to server every 30 seconds

/**
 * Initializes the PeerJS connection for the local client.
 * Connects to the PeerJS server and gets a unique ID.
 * @returns {Promise<string>} A promise that resolves with the local client's peerId.
 */
export function initializePeer() {
  // If peer exists, is open, and not destroyed, consider it initialized.
  if (peer && myPeerId && peer.open && !peer.destroyed) {
    return Promise.resolve(myPeerId);
  }

  if (initializePromise) {
    console.warn("PeerJS: initializePeer called while an initialization is already in progress. Returning existing promise.");
    return initializePromise;
  }

  initializePromise = new Promise((resolve, reject) => {
    // Clean up old peer instance if it exists.
    if (peer) {
        console.warn("PeerJS: Existing peer object found during re-initialization. Attempting to destroy it first.");
        peer.destroy(); // Ensures old resources (e.g., WebSocket connection) are cleaned up.
        // Nullify peer here as destroy is synchronous in effect for new connections.
        // myPeerId will be reset on successful new 'open' or handled in 'close'.
    }
    peer = null; // Ensure we are starting fresh for the new Peer object

    console.log("PeerJS: Creating new Peer instance.");
    // Create a new Peer instance.
    // Connect to the official public PeerJS server instead of local server
    peer = new Peer(null, { // Pass null for the ID to let the server assign one
      // Use the official PeerJS cloud server instead of localhost
      // This eliminates the need to run a local PeerJS server
      debug: 1             // Reduced from 3 to minimize logging
    });

    const cleanupAndEndInitialization = (success = true) => {
        if (!success && peer) { // If initialization failed, ensure this specific peer instance is destroyed
            peer.destroy();
        }
        initializePromise = null; // Clear the stored promise
    };

    peer.on('open', (id) => {
      console.log('My peer ID is: ' + id);
      myPeerId = id;
      startServerKeepAlive(); // Start server keepalive when connected
      resolve(id);
      cleanupAndEndInitialization(true);
    });

    peer.on('connection', (conn) => {
      console.log(`Incoming connection from ${conn.peer}`);
      setupConnectionEventHandlers(conn);
    });

    peer.on('disconnected', () => {
      console.warn('PeerJS: Disconnected from signaling server.');
      if (peer && !peer.destroyed && peer.disconnected) {
        console.log('PeerJS: Manually calling peer.reconnect() on disconnected event.');
        peer.reconnect();
        // Re-establish server keepalive after reconnection attempt
        startServerKeepAlive();
      } else if (peer) {
        console.log(`PeerJS: Not attempting manual reconnect on disconnected. Destroyed: ${peer.destroyed}, Disconnected: ${peer.disconnected}`);
      }
      // If reconnect fails, it will eventually lead to a 'close' event if not recovered.
    });

    peer.on('close', () => {
      const peerState = peer ? `open=${peer.open}, disconnected=${peer.disconnected}, destroyed=${peer.destroyed}` : 'peer object was null';
      console.warn(`PeerJS: Peer object closed (connection to server permanently lost). State: ${peerState}`);
      
      myPeerId = null; // Peer is no longer valid
      // Do not set global `peer` to null here, `destroy()` handles cleanup.
      // New `initializePeer` call will create a new one if needed.
      stopKeepAliveChecks(); 
      stopServerKeepAlive(); // Stop server keepalive
      
      if (initializePromise) { // If this close happens during an active initialization attempt
         reject(new Error("PeerJS: Connection closed during initialization."));
      }
      cleanupAndEndInitialization(false); // Mark as failed initialization if it was in progress
    });
    
    peer.on('error', (err) => {
      const peerState = peer ? `open=${peer.open}, disconnected=${peer.disconnected}, destroyed=${peer.destroyed}` : 'peer object is null';
      console.error('PeerJS error:', err, `Peer state: ${peerState}`);
      
      // If initialization fails and we don't have an ID yet (meaning 'open' hasn't fired for this instance).
      if (!myPeerId && initializePromise) {
        reject(err); // This will propagate to the caller of initializePeer()
        cleanupAndEndInitialization(false); // Mark as failed initialization
      } else if (err.type === 'peer-unavailable') {
        console.error(`Peer ${err.message.split(' ')[2]} is unavailable.`);
      }
      // Other errors might be transient or related to specific connections, not necessarily fatal to the Peer object itself.
      // If an error is fatal, it usually triggers a 'close' or 'disconnected' event.
    });
  });
  return initializePromise;
}

/**
 * Checks if the PeerJS instance is initialized and has a Peer ID.
 * @returns {boolean} True if initialized, false otherwise.
 */
export function isPeerInitialized() {
    return peer && myPeerId && peer.open && !peer.destroyed;
}

/**
 * Sets up event handlers for a new DataConnection (either incoming or outgoing).
 * @param {Peer.DataConnection} conn - The PeerJS DataConnection object.
 */
function setupConnectionEventHandlers(conn) {
  if (pendingDisconnectTimers[conn.peer]) {
    clearTimeout(pendingDisconnectTimers[conn.peer]);
    delete pendingDisconnectTimers[conn.peer];
    console.log(`Peer ${conn.peer} reconnected before prune grace elapsed.`);
  }
  connections[conn.peer] = conn;
  lastContactTimestamps[conn.peer] = Date.now(); // Initialize last contact time
  console.log(`Connection established with ${conn.peer}. Ready to send/receive data.`);

  conn.on('open', () => {
    console.log(`Data connection opened with ${conn.peer}`);
    lastContactTimestamps[conn.peer] = Date.now(); 
  });

  conn.on('data', (data) => {
    // console.log(`Received data from ${conn.peer}:`, data);
    lastContactTimestamps[conn.peer] = Date.now(); // Update on any data

    if (data.type === 'PING') {
      // console.log(`Received PING from ${conn.peer}, sending PONG.`);
      sendMessageToPeer(conn.peer, { type: 'PONG', timestamp: data.timestamp });
    } else if (data.type === 'PONG') {
      // console.log(`Received PONG from ${conn.peer}`);
      // lastContactTimestamps already updated at the start of 'data' event.
    } else if (onDataReceivedCallback) {
      // Pass other data types to the application logic
      onDataReceivedCallback(conn.peer, data);
    }
  });

  conn.on('close', () => {
    console.log(`Connection closed with ${conn.peer}`);
    delete connections[conn.peer];
    delete lastContactTimestamps[conn.peer];
    schedulePeerDisconnected(conn.peer, 'connection_closed');
    // UI update will be triggered by chat.js listening to this or by timeout check
  });

  conn.on('error', (err) => {
    console.error(`Connection error with ${conn.peer}:`, err);
    // No need to delete connections[conn.peer] here, 'close' will often be called after 'error'.
    // If not, the timeout check will eventually catch it.
  });
}

function schedulePeerDisconnected(peerId, reason) {
  if (!peerId || pendingDisconnectTimers[peerId]) return;
  pendingDisconnectTimers[peerId] = setTimeout(() => {
    delete pendingDisconnectTimers[peerId];
    const replacement = connections[peerId];
    if (replacement && replacement.open) {
      console.log(`Peer ${peerId} prune cancelled; replacement connection is open.`);
      return;
    }
    if (onPeerDisconnectedCallback) {
      try {
        onPeerDisconnectedCallback(peerId, { reason, graceMs: PEER_PRUNE_GRACE_MS });
      } catch (err) {
        console.error(`Error in peer disconnected callback for ${peerId}:`, err);
      }
    }
  }, PEER_PRUNE_GRACE_MS);
}

/**
 * Connects to a remote peer using their peerId.
 * @param {string} targetPeerId - The peerId of the remote client to connect to.
 * @returns {Peer.DataConnection | null} The DataConnection object if connection initiated, otherwise null.
 */
export function connectToPeer(targetPeerId) {
  if (!peer || peer.destroyed) {
    console.error('PeerJS not initialized, peer was closed, or peer is destroyed. Call initializePeer() or re-initialize if necessary.');
    return null;
  }
  if (!peer.open && !peer.disconnected) { // If disconnected, reconnect logic should be handling it.
      console.warn('PeerJS: Attempting connectToPeer, but peer connection to server is not open (and not in disconnected state). This may fail.');
  } else if (peer.disconnected) {
      console.warn('PeerJS: Attempting connectToPeer while peer is disconnected from server. Reconnection should be in progress.');
  }

  if (targetPeerId === myPeerId) {
    console.warn("Cannot connect to self.");
    return null;
  }

  const existingConn = connections[targetPeerId];
  if (existingConn) {
    if (existingConn.open) {
      console.log(`Already connected to ${targetPeerId}. Reusing existing open connection.`);
      return existingConn;
    } else {
      // Existing connection is not open. Clean it up before trying a new one.
      console.warn(`Found existing, non-open connection to ${targetPeerId} (readyState: ${existingConn.readyState}). Closing and removing it before attempting a fresh connection.`);
      delete connections[targetPeerId]; // Remove from map first
      if (lastContactTimestamps[targetPeerId]) {
          delete lastContactTimestamps[targetPeerId]; // Clean up timestamp
      }
      existingConn.close(); // Tell the connection to close its underlying resources
    }
  }

  console.log(`Attempting to connect to peer: ${targetPeerId}`);
  const newConn = peer.connect(targetPeerId, {
    reliable: true,
  });

  if (newConn) {
    // setupConnectionEventHandlers will add newConn to connections[targetPeerId]
    setupConnectionEventHandlers(newConn);
  } else {
    console.error(`Failed to initiate connection to ${targetPeerId}. Peer.connect() returned null.`);
  }
  return newConn;
}

/**
 * Sends a message to a specific connected peer.
 * @param {string} targetPeerId - The peerId of the recipient.
 *   @param {any} data - The data to send (must be serializable, e.g., JSON).
 */
export function sendMessageToPeer(targetPeerId, data) {
  const conn = connections[targetPeerId];
  if (conn && conn.open) {
    conn.send(data);
  } else {
    console.warn(`Not connected to peer ${targetPeerId} or connection not open.`);
  }
}

/**
 * Broadcasts a message to all currently connected peers.
 * @param {any} data - The data to send (must be serializable).
 */
export function broadcastMessage(data) {
  Object.values(connections).forEach((conn) => {
    if (conn && conn.open) {
      conn.send(data);
    }
  });
}

/**
 * Registers a callback function to be invoked when data is received from any peer.
 * @param {function(string, any)} callback - The function to call. It will receive (senderPeerId, data).
 */
export function onDataReceived(callback) {
  onDataReceivedCallback = callback;
}

/**
 * Registers a callback function invoked when a peer connection closes.
 * @param {function(string, object=): void} callback
 */
export function onPeerDisconnected(callback) {
  onPeerDisconnectedCallback = callback;
}

/**
 * Gets the current local Peer ID.
 * @returns {string | null} The local Peer ID, or null if not initialized.
 */
export function getMyPeerId() {
    return myPeerId;
}

/**
 * Gets the PeerJS instance.
 * @returns {Peer | null} The Peer instance, or null if not initialized.
 */
export function getPeerInstance() {
    return peer;
}

/**
 * Gets the current active connections.
 * @returns {object} A dictionary of active connections.
 */
export function getActiveConnections() {
    return connections;
}

export function startKeepAliveChecks() {
  if (pingIntervalId) clearInterval(pingIntervalId);
  if (timeoutCheckIntervalId) clearInterval(timeoutCheckIntervalId);

  // Start server keepalive when starting peer keepalive
  startServerKeepAlive();

  // Interval to send PINGs
  pingIntervalId = setInterval(() => {
    Object.keys(connections).forEach(peerId => {
      if (connections[peerId] && connections[peerId].open) {
        // console.log(`Sending PING to ${peerId}`);
        sendMessageToPeer(peerId, { type: 'PING', timestamp: Date.now() });
      }
    });
  }, PING_INTERVAL);

  // Interval to check for PONG timeouts
  timeoutCheckIntervalId = setInterval(() => {
    const now = Date.now();
    Object.keys(connections).forEach(peerId => {
      const conn = connections[peerId];
      if (conn && conn.open) { // Only check open connections
        const lastContact = lastContactTimestamps[peerId] || 0;
        if (now - lastContact > PONG_TIMEOUT) {
          console.warn(`Peer ${peerId} timed out (no PONG or other data received). Closing connection.`);
          conn.close(); // This will trigger the 'close' event handler
        }
      }
    });
  }, PING_INTERVAL / 2); // Check more frequently than pings are sent

  console.log('Keep-alive checks started.');
}

export function stopKeepAliveChecks() {
  if (pingIntervalId) clearInterval(pingIntervalId);
  if (timeoutCheckIntervalId) clearInterval(timeoutCheckIntervalId);
  Object.keys(pendingDisconnectTimers).forEach(peerId => {
    clearTimeout(pendingDisconnectTimers[peerId]);
    delete pendingDisconnectTimers[peerId];
  });
  pingIntervalId = null;
  timeoutCheckIntervalId = null;
  console.log('Keep-alive checks stopped.');
}

/**
 * Starts a keepalive mechanism to maintain connection to the PeerJS server
 */
function startServerKeepAlive() {
  if (serverKeepAliveIntervalId) clearInterval(serverKeepAliveIntervalId);
  
  serverKeepAliveIntervalId = setInterval(() => {
    if (peer && peer.open && !peer.destroyed) {
      // PeerJS doesn't have a built-in server ping, but we can check the connection state
      // and attempt to reconnect if needed
      if (peer.disconnected) {
        console.log('PeerJS: Detected disconnected state during keepalive check. Attempting reconnect...');
        peer.reconnect();
      }
    } else if (peer && peer.destroyed) {
      console.warn('PeerJS: Peer destroyed during keepalive check. Stopping keepalive.');
      stopServerKeepAlive();
    }
  }, SERVER_KEEPALIVE_INTERVAL);
  
  console.log('Server keep-alive checks started.');
}

/**
 * Stops the server keepalive mechanism
 */
function stopServerKeepAlive() {
  if (serverKeepAliveIntervalId) {
    clearInterval(serverKeepAliveIntervalId);
    serverKeepAliveIntervalId = null;
    console.log('Server keep-alive checks stopped.');
  }
}

/**
 * Tests if a peer is reachable by attempting a quick connection
 * @param {string} targetPeerId - The peerId to test
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<boolean>} - Resolves to true if peer is reachable, false otherwise
 */
export async function testPeerReachability(targetPeerId, timeout = 5000) {
  return new Promise((resolve) => {
    if (!peer || peer.destroyed || !peer.open) {
      console.warn('Cannot test peer reachability: local peer not ready');
      resolve(false);
      return;
    }

    const testConn = peer.connect(targetPeerId, { reliable: true });
    let timeoutId = null;
    let resolved = false;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (testConn && !testConn.destroyed) testConn.close();
      resolved = true;
    };

    const handleResult = (reachable) => {
      if (!resolved) {
        cleanup();
        resolve(reachable);
      }
    };

    testConn.on('open', () => {
      console.log(`Peer ${targetPeerId} is reachable`);
      handleResult(true);
    });

    testConn.on('error', (err) => {
      console.log(`Peer ${targetPeerId} is not reachable:`, err.message);
      handleResult(false);
    });

    timeoutId = setTimeout(() => {
      console.log(`Peer ${targetPeerId} reachability test timed out`);
      handleResult(false);
    }, timeout);
  });
} 
